/**
 * <MoveSessionFlow> — « Décaler une séance », de bout en bout (template).
 *
 * Quelle séance ? → le créneau de rattrapage, ou « Je choisirai plus tard » → confirmation.
 * Deux détours, jamais une impasse :
 *  - séance à moins de 2 h → « Trop tard pour décaler », puis le parent peut encore PRÉVENIR
 *    D'UNE ABSENCE (le backend l'accepte : le drapeau est posé, l'élève reste dans la séance, et
 *    celle-ci n'est pas rattrapable) ou signaler un retard. Exactement deux boutons : le message
 *    au tuteur n'a rien à faire ici, et une étape de geste n'en porte jamais plus de deux ;
 *  - cours individuel ou séance d'essai → on renvoie vers le tuteur ou vers l'équipe, parce que
 *    le formulaire d'absence ne couvre que les cours en groupe.
 *
 * Contrôlé : l'étape courante, la séance et le créneau choisis arrivent en props, les gestes
 * repartent en callbacks. La machine à états vit côté écran — c'est ce qui rend chaque étape
 * regardable seule en story.
 *
 * Toute la copie du geste est ICI, dans ses deux voix (`TEXTS`). Le parent est vouvoyé et lit son
 * enfant à la troisième personne. L'élève (`voice="student"`) lit les mots de son formulaire
 * d'absence legacy (`student/absence/*`), à la première personne là où celui-ci le fait parler
 * (« Je vais être absent.e à la séance du … ») ; les phrases que seul le design system prononce
 * sont tutoyées. Une séance déjà commencée se dit commencée, chez l'élève seulement.
 *
 * Dans les deux voix, comme dans le formulaire legacy que le parent remplissait aussi
 * (`withStudent`) :
 *  - l'encadré « Absences » (`absenceQuota`) se lit avant de confirmer ; quand l'absence ne se
 *    rattrapera pas et qu'il n'y a aucun créneau, il dit tout, et la liste ne s'affiche pas ;
 *  - sans créneau à choisir, le bouton principal signale l'absence — le « Confirmer » legacy restait
 *    actif (Wajih, 23/10 pour le parent) ; « Je choisirai plus tard » ne s'offre qu'avec des créneaux.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { ABSENCE_STILL_REPORTABLE_HINT, AbsenceQuotaNotice } from "../../molecules/AbsenceQuotaNotice"
import { RecoverySlotStep } from "../../organisms/RecoverySlotStep"
import { SessionPickerStep } from "../../organisms/SessionPickerStep"
import { type UIAbsenceQuota, type UILiveSession, type UIParentRecoverySlot } from "../../../ports/primary/ui/types"
import { recoverySlotStepShape } from "../../../ports/primary/ui/formatters/absenceQuotaFormatter"
import { elide } from "../../../ports/primary/ui/formatters/childFormatter"
import { formatDayTime, minutesUntil } from "../../../ports/primary/ui/formatters/dateFormatter"
import { canMoveSession, moveCutoffLabel } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type Addressee } from "../../utils/addressee"
import { type Voice } from "../../utils/voice"
import { ParentFlowModal } from "../ParentFlowModal"
import { MANAGE_RDV_TEXTS } from "../SessionGestureDialog/sessionGestureTexts"

/**
 * Les branches du geste. `contactTutor` couvre le cours individuel et la séance d'essai ;
 * `absence` / `absenceDone` sont la sortie des moins de 2 h — prévenir d'une absence qu'on ne
 * pourra pas rattraper mérite qu'on la confirme, et qu'on l'accuse.
 */
export type MoveSessionStep = "session" | "slot" | "tooLate" | "absence" | "absenceDone" | "contactTutor" | "done"

/** Ce que le type de salle impose : un individuel se règle avec le tuteur, un essai avec l'équipe. */
export type ContactTutorKind = "individual" | "trial"

const TOTAL_STEPS = 2

/** Une phrase et sa ligne discrète, telles que `<FlowOutcome>` les pose. */
type Outcome = { message: string; detail?: string }

/** Ce que l'étape des créneaux annonce du rattrapage (`recoverySlotStepShape`). */
type RecoveryOutcome = ReturnType<typeof recoverySlotStepShape>["outcome"]

/** L'accusé du formulaire legacy de l'élève (`AbsenceFormComponent`), et ses deux issues sans rattrapage. */
const DONE_TITLE = "Effectué"
const ABSENCE_REPORTED = "L'absence a bien été signalée"
const NOT_RECOVERABLE = "La séance ne pourra pas être rattrapée."

/**
 * Les mots du geste, par voix. `child` est le prénom de l'enfant — vide en voix élève, où aucune
 * phrase ne le lit. `refused` : le rattrapage est refusé pour de bon ; `nothingToPick` : aucun
 * créneau n'est proposé.
 */
const TEXTS: Record<
    Voice,
    {
        /** Le rappel d'une étape qui n'en a pas d'autre — « pour Damien ». */
        reminder: (child: string) => string | undefined
        noSession: (child: string) => string
        slotTitle: (nothingToPick: boolean) => string
        /**
         * `outcome` : ce que la ligne annonce (`recoverySlotStepShape`) ; `refused` : le rattrapage
         * est refusé pour de bon — l'élève garde la ligne de son ancien formulaire, qui ne lit que lui.
         */
        slotContext: (sessionLabel: string, outcome: RecoveryOutcome, refused: boolean) => string
        tooLate: (sessionLabel: string, child: string, started: boolean) => Outcome
        absenceMessage: (sessionLabel: string, child: string) => string
        absenceSubmit: string
        absenceDoneTitle: string
        absenceDone: (sessionLabel: string, child: string, tutor: string) => Outcome
        trial: { message: string; whatsApp: string; email: string }
        individual: { message: string; contact: (tutorFirstname: string) => string }
        doneTitle: (chosenLater: boolean) => string
        doneWithSlot: (sessionLabel: string, slotLabel: string) => Outcome
        doneLater: (sessionLabel: string, refused: boolean) => Outcome
    }
> = {
    parent: {
        reminder: (child) => `pour ${child}`,
        noSession: (child) => `${child} n'a aucune séance à venir pour le moment.`,
        slotTitle: () => "Quel nouveau créneau ?",
        // La phrase dit ce qui se passera : un créneau choisi ici, un rattrapage plus tard, aucun —
        // et rien de plus que l'absence quand l'encadré dit pourquoi, ou qu'on ne le sait pas encore.
        slotContext: (sessionLabel, outcome) => {
            if (outcome === "refused")
                return `La séance du ${sessionLabel} sera notée absente et ne sera pas rattrapée.`
            if (outcome === "later")
                return `La séance du ${sessionLabel} sera notée absente ; le rattrapage se planifiera plus tard.`
            if (outcome === "slot")
                return `La séance du ${sessionLabel} sera notée absente et rattrapée sur le créneau choisi.`
            return `La séance du ${sessionLabel} sera notée absente.`
        },
        tooLate: (sessionLabel, child) => ({
            message: `La séance du ${sessionLabel} commence dans moins de 2 h : elle ne peut plus être décalée.`,
            detail: `Vous pouvez encore prévenir ${elide(child, "que")} ne viendra pas, ou annoncer un retard.`,
        }),
        absenceMessage: (sessionLabel, child) => `${child} ne sera pas à la séance du ${sessionLabel}.`,
        absenceSubmit: "Prévenir de l'absence",
        absenceDoneTitle: "Absence notée",
        absenceDone: (sessionLabel, child, tutor) => ({
            message: `L'absence ${elide(child)} à la séance du ${sessionLabel} est notée. Cette séance ne pourra pas être rattrapée.`,
            detail: `${tutor} la verra sur sa liste d'élèves au début de la séance.`,
        }),
        trial: {
            message: "Une séance d'essai se règle avec notre équipe, qui vous répond directement.",
            whatsApp: "Écrire sur WhatsApp",
            email: "Nous écrire par e-mail",
        },
        individual: {
            message: "Un cours individuel se règle directement avec le tuteur.",
            contact: (tutorFirstname) => `Écrire à ${tutorFirstname}`,
        },
        doneTitle: (chosenLater) => (chosenLater ? "Absence notée" : "Séance décalée"),
        doneWithSlot: (sessionLabel, slotLabel) => ({
            message: `La séance du ${sessionLabel} est rattrapée le ${slotLabel}.`,
        }),
        // Un rattrapage refusé ne « reste » pas à planifier : la phrase dit ce qui a été posé.
        doneLater: (sessionLabel, refused) => ({
            message: refused
                ? `L'absence du ${sessionLabel} est notée. Cette séance ne pourra pas être rattrapée.`
                : `L'absence du ${sessionLabel} est notée. Le rattrapage reste à planifier : vous le retrouverez dans « À faire ».`,
        }),
    },
    student: {
        reminder: () => undefined,
        noSession: () => "Tu n'as aucune séance à venir pour le moment.",
        slotTitle: (nothingToPick) => (nothingToPick ? "Récapitulatif" : "Je choisis une séance de rattrapage"),
        slotContext: (sessionLabel, _outcome, refused) =>
            refused ? `Date de l'absence : ${sessionLabel}` : `Je vais être absent.e à la séance du ${sessionLabel}.`,
        tooLate: (sessionLabel, _child, started) => ({
            message: started
                ? `La séance du ${sessionLabel} a déjà commencé : elle ne peut plus être décalée.`
                : `La séance du ${sessionLabel} commence dans moins de 2 h : elle ne peut plus être décalée.`,
            detail: ABSENCE_STILL_REPORTABLE_HINT,
        }),
        absenceMessage: (sessionLabel) => `Je vais être absent.e à la séance du ${sessionLabel}.`,
        absenceSubmit: "Signaler l'absence",
        absenceDoneTitle: DONE_TITLE,
        absenceDone: () => ({ message: ABSENCE_REPORTED, detail: NOT_RECOVERABLE }),
        trial: {
            message: MANAGE_RDV_TEXTS.student.trial.message,
            whatsApp: MANAGE_RDV_TEXTS.student.trial.whatsApp,
            email: MANAGE_RDV_TEXTS.student.trial.email,
        },
        individual: {
            message: MANAGE_RDV_TEXTS.student.contactTutor.message,
            contact: () => MANAGE_RDV_TEXTS.student.contactTutor.contact,
        },
        doneTitle: () => DONE_TITLE,
        doneWithSlot: (sessionLabel, slotLabel) => ({
            message: ABSENCE_REPORTED,
            detail: `La séance du ${sessionLabel} est rattrapée le ${slotLabel}.`,
        }),
        doneLater: (_sessionLabel, refused) => ({
            message: ABSENCE_REPORTED,
            detail: refused ? NOT_RECOVERABLE : "Le rattrapage reste à planifier.",
        }),
    },
}

export type MoveSessionFlowProps = Addressee & {
    visible: boolean
    onClose: () => void
    step: MoveSessionStep

    sessions: UILiveSession[]
    selectedSession?: UILiveSession | null
    onSelectSession: (session: UILiveSession) => void

    slots?: UIParentRecoverySlot[]
    slotsLoading?: boolean
    selectedSlot?: UIParentRecoverySlot | null
    onSelectSlot?: (slot: UIParentRecoverySlot) => void
    /** « Je choisirai plus tard » — l'absence est notée, le rattrapage revient dans « À faire ». */
    onChooseSlotLater?: () => void
    /** Remplace la phrase de l'étape vide quand le rattrapage est refusé pour de bon. */
    slotsEmptyMessage?: string
    /**
     * Le quota d'absences, montré AVANT de confirmer, à l'étape des créneaux et à la confirmation
     * d'une absence à moins de 2 h (l'encadré « Absences » du formulaire legacy). Absent : pas
     * d'encadré — le serveur n'a pas servi de plafond, ou la lecture n'est pas encore revenue.
     */
    absenceQuota?: UIAbsenceQuota

    onSubmit?: () => void
    submitLoading?: boolean
    onBack?: () => void

    /** Sorties de l'écran « trop tard ». */
    onReportLate?: () => void
    onMessageTutor?: () => void
    /** « Prévenir d'une absence » depuis « trop tard » — mène à l'étape de confirmation. */
    onDeclareAbsence?: () => void
    /** Confirme l'absence à moins de 2 h : le drapeau part, la séance n'est pas rattrapable. */
    onSubmitAbsence?: () => void
    absenceLoading?: boolean

    /** Branche cours individuel / essai. */
    contactKind?: ContactTutorKind
    onContactWhatsApp?: () => void
    onContactEmail?: () => void

    /** Vrai quand le parent a renoncé à choisir un créneau — change le mot de la confirmation. */
    chosenLater?: boolean

    /** Ce qui ne passe pas : un envoi refusé, ou le motif pour lequel le serveur ne propose rien. */
    errorMessage?: string
    /** `alert` (défaut) pour un échec d'envoi, `warning` pour un refus attendu du serveur. */
    errorSeverity?: "alert" | "warning"
    testID?: string
}

export function MoveSessionFlow(props: MoveSessionFlowProps) {
    const {
        visible,
        onClose,
        step,
        voice = "parent",
        childFirstname,
        sessions,
        selectedSession,
        onSelectSession,
        slots = [],
        slotsLoading = false,
        selectedSlot,
        onSelectSlot,
        onChooseSlotLater,
        slotsEmptyMessage,
        absenceQuota,
        onSubmit,
        submitLoading = false,
        onBack,
        onReportLate,
        onMessageTutor,
        onDeclareAbsence,
        onSubmitAbsence,
        absenceLoading = false,
        contactKind = "individual",
        onContactWhatsApp,
        onContactEmail,
        chosenLater = false,
        errorMessage,
        errorSeverity,
        testID,
    } = props

    const texts = TEXTS[voice]
    const child = childFirstname ?? ""
    const tutorFirstname = selectedSession?.tutor.firstname ?? "votre tuteur"
    /** Même prénom, mais en tête de phrase : « votre tuteur la verra » ne s'écrit pas. */
    const tutorSubject = selectedSession?.tutor.firstname ?? "Le tuteur"
    const sessionLabel = selectedSession != null ? formatDayTime(selectedSession.date, false, "sentence") : ""
    const sessionStarted = selectedSession != null && minutesUntil(selectedSession.date) <= 0

    const quotaNotice =
        absenceQuota != null ? (
            <AbsenceQuotaNotice quota={absenceQuota} voice={voice} childFirstname={childFirstname} />
        ) : null
    const {
        nothingToPick,
        quotaSaysItAll,
        refused: recoveryRefused,
        outcome: recoveryOutcome,
        submitsWithoutSlot,
    } = recoverySlotStepShape({
        slotCount: slots.length,
        loading: slotsLoading,
        quota: absenceQuota,
        refusedForGood: slotsEmptyMessage != null,
        errorShown: errorMessage != null,
    })

    const cancel = <Button label="Annuler" variant="outline" onPress={onClose} />
    const close = <Button label="Fermer" variant="outline" onPress={onClose} />

    const content = (() => {
        switch (step) {
            case "session":
                return {
                    stepTitle: "Quelle séance ?",
                    stepIndex: 1,
                    context: undefined,
                    footer: cancel,
                    body: (
                        <SessionPickerStep
                            sessions={sessions}
                            selectedId={selectedSession?.id}
                            onSelect={onSelectSession}
                            badgeFor={(session) => (canMoveSession(session) ? undefined : "Dans moins de 2 h")}
                            // La règle des 2 h dite en heure, sur une séance d'aujourd'hui qu'on peut encore
                            // décaler — les jours suivants, la limite est loin et la mention chargerait la liste.
                            noteFor={(session) => {
                                const cutoff = moveCutoffLabel(session)
                                return cutoff != null ? `Possible jusqu'à ${cutoff}` : undefined
                            }}
                            emptyMessage={texts.noSession(child)}
                        />
                    ),
                }

            case "slot": {
                const slotList = quotaSaysItAll ? null : (
                    <RecoverySlotStep
                        slots={slots}
                        loading={slotsLoading}
                        selectedId={selectedSlot?.id}
                        onSelect={(slot) => onSelectSlot?.(slot)}
                        onChooseLater={submitsWithoutSlot ? undefined : onChooseSlotLater}
                        emptyMessage={slotsEmptyMessage}
                        voice={voice}
                    />
                )
                return {
                    stepTitle: texts.slotTitle(nothingToPick),
                    stepIndex: 2,
                    context: texts.slotContext(sessionLabel, recoveryOutcome, recoveryRefused),
                    footer: (
                        <Stack gap="2">
                            {submitsWithoutSlot ? (
                                // Aucun créneau à choisir : l'absence se signale, avec les mots de ce geste.
                                <Button
                                    label={texts.absenceSubmit}
                                    onPress={onChooseSlotLater}
                                    loading={submitLoading}
                                    disabled={submitLoading}
                                    fullWidth
                                />
                            ) : (
                                <Button
                                    label="Décaler la séance"
                                    onPress={onSubmit}
                                    loading={submitLoading}
                                    disabled={selectedSlot == null || submitLoading}
                                    fullWidth
                                />
                            )}
                            {cancel}
                        </Stack>
                    ),
                    body:
                        quotaNotice == null ? (
                            slotList
                        ) : (
                            <Stack gap="4">
                                {quotaNotice}
                                {slotList}
                            </Stack>
                        ),
                }
            }

            case "tooLate":
                return {
                    stepTitle: "Trop tard pour décaler",
                    stepIndex: 2,
                    context: undefined,
                    // Deux boutons suffisent : la croix du titre ferme déjà la feuille.
                    footer: undefined,
                    body: (
                        <FlowOutcome
                            iconName="hourglass-half"
                            tone="warning"
                            {...texts.tooLate(sessionLabel, child, sessionStarted)}
                        >
                            {/* Deux boutons, pas trois : le message au tuteur est un autre sujet,
                                et une étape de geste ne propose jamais plus de deux actions. */}
                            <Stack gap="2">
                                <Button label="Prévenir d'une absence" onPress={onDeclareAbsence} fullWidth />
                                <Button label="Signaler un retard" variant="outline" onPress={onReportLate} fullWidth />
                            </Stack>
                        </FlowOutcome>
                    ),
                }

            case "absence":
                return {
                    stepTitle: "Prévenir d'une absence",
                    stepIndex: 2,
                    context: undefined,
                    footer: (
                        <Stack gap="2">
                            <Button
                                label={texts.absenceSubmit}
                                onPress={onSubmitAbsence}
                                loading={absenceLoading}
                                disabled={absenceLoading}
                                fullWidth
                            />
                            {cancel}
                        </Stack>
                    ),
                    body: (
                        <FlowOutcome
                            iconName="hourglass-half"
                            tone="warning"
                            message={texts.absenceMessage(sessionLabel, child)}
                            // L'encadré, quand il est là, le dit déjà avec sa raison : on ne le répète pas.
                            detail={
                                quotaNotice == null
                                    ? "À moins de 2 h, cette séance ne pourra pas être rattrapée."
                                    : undefined
                            }
                        >
                            {quotaNotice}
                        </FlowOutcome>
                    ),
                }

            case "absenceDone":
                return {
                    stepTitle: texts.absenceDoneTitle,
                    stepIndex: 2,
                    context: undefined,
                    footer: close,
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            {...texts.absenceDone(sessionLabel, child, tutorSubject)}
                        />
                    ),
                }

            case "contactTutor":
                return contactKind === "trial"
                    ? {
                          stepTitle: "Séance d'essai",
                          stepIndex: 2,
                          context: undefined,
                          footer: close,
                          body: (
                              <FlowOutcome iconName="comment" message={texts.trial.message}>
                                  <Stack gap="2">
                                      <Button label={texts.trial.whatsApp} onPress={onContactWhatsApp} fullWidth />
                                      <Button
                                          label={texts.trial.email}
                                          variant="outline"
                                          onPress={onContactEmail}
                                          fullWidth
                                      />
                                  </Stack>
                              </FlowOutcome>
                          ),
                      }
                    : {
                          stepTitle: "Séance individuelle",
                          stepIndex: 2,
                          context: undefined,
                          footer: close,
                          body: (
                              <FlowOutcome iconName="comment" message={texts.individual.message}>
                                  {/* Sans geste pour écrire au tuteur, l'étape l'annonce et se ferme. */}
                                  {onMessageTutor != null && (
                                      <Button
                                          label={texts.individual.contact(tutorFirstname)}
                                          onPress={onMessageTutor}
                                          fullWidth
                                      />
                                  )}
                              </FlowOutcome>
                          ),
                      }

            case "done":
            default:
                return {
                    stepTitle: texts.doneTitle(chosenLater),
                    stepIndex: 2,
                    context: undefined,
                    footer: close,
                    body: (
                        <FlowOutcome
                            iconName="check"
                            tone="success"
                            {...(chosenLater
                                ? texts.doneLater(sessionLabel, recoveryRefused)
                                : texts.doneWithSlot(
                                      sessionLabel,
                                      selectedSlot != null ? formatDayTime(selectedSlot.date, false, "sentence") : ""
                                  ))}
                        />
                    ),
                }
        }
    })()

    return (
        <ParentFlowModal
            visible={visible}
            onClose={onClose}
            title="Décaler une séance"
            stepTitle={content.stepTitle}
            step={content.stepIndex}
            totalSteps={TOTAL_STEPS}
            onBack={step === "session" || step === "done" || step === "absenceDone" ? undefined : onBack}
            context={content.context ?? texts.reminder(child)}
            errorMessage={errorMessage}
            errorSeverity={errorSeverity}
            footer={content.footer}
            testID={testID}
        >
            {content.body}
        </ParentFlowModal>
    )
}
